import { BenefitsEnums } from "models/enums";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { useMemo } from "react";
import { TranslationKeys } from "translations";
import { EnumHelper, LocalizationHelperInstance } from "utils/helpers";
import { WintSuperkollAPIModelWintSalaryBenefits } from "wint-api-client/dist/types";

const { BenefitType } = BenefitsEnums;

// A deliberate picker order, not enum order.
const BenefitTypeDisplayOrder: BenefitsEnums.BenefitType[] = [
  BenefitType.Car,
  BenefitType.GrossDeduction,
  BenefitType.NetDeduction,
  BenefitType.CarNetDeduction,
  BenefitType.NetDeductionInsurance,
  BenefitType.NetDeductionNoVat,
  BenefitType.ResidenceSmallNetDeduction,
  BenefitType.ResidenceNonSmallNetDeduction,
  BenefitType.SalarySacrifice,
  BenefitType.ResidenceSmallGrossIncrease,
  BenefitType.ResidenceNonSmallGrossIncrease,
  BenefitType.RentalCompensation,
  BenefitType.Common,
];

export { getDefaultBenefitFrequency, hasBenefitValue, isDeductionBenefitType } from "./benefitTypeRules";

export const getBenefitTypeName = (type?: number | string) =>
  LocalizationHelperInstance.getTranslatedEnumValue("Benefit.Type", type);

export const withDeductionWording = (frequencyChoices: SelectionItem[]): SelectionItem[] =>
  frequencyChoices.map((choice) =>
    choice.id === BenefitsEnums.BenefitFrequency.Once
      ? { ...choice, value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Benefit.OneTimeDeduction) }
      : choice
  );

function useBenefitTypesHelper() {
  const frequencyChoices: SelectionItem[] = useMemo(
    () =>
      EnumHelper.getNumericItemValues(BenefitsEnums.BenefitFrequency).map((value) => ({
        id: value,
        value: LocalizationHelperInstance.getTranslatedEnumValue("Benefit.Frequency", value),
      })),
    []
  );

  const benefitTypes: AutocompleteListItem[] = useMemo(() => {
    // A type added to the enum but not to the order list still appears, last, rather than vanishing from the picker.
    const unorderedTypes = EnumHelper.getNumericItemValues(BenefitsEnums.BenefitType).filter(
      (type) => !BenefitTypeDisplayOrder.includes(type)
    );
    return [...BenefitTypeDisplayOrder, ...unorderedTypes].map((type) => ({
      id: type,
      value: getBenefitTypeName(type),
    }));
  }, []);

  const groupSelections: SelectionItem[] = useMemo(() => {
    return EnumHelper.getNumericItemValues(WintSuperkollAPIModelWintSalaryBenefits.BenefitGroupSelection).map(
      (value) => ({
        id: value,
        value: LocalizationHelperInstance.getTranslatedEnumValue("Benefit.GroupSelection", value),
      })
    );
  }, []);

  const carBenefitSearchMethodChoices: SelectionItem[] = useMemo(() => {
    return EnumHelper.getNumericItemValues(BenefitsEnums.CarBenefitSearchMethod).map((value) => ({
      id: value,
      value: LocalizationHelperInstance.getTranslatedEnumValue("Benefit.CarBenefitSearchMethod", value),
    }));
  }, []);

  return {
    frequencyChoices,
    benefitTypes,
    carBenefitSearchMethodChoices,
    groupSelections,
  };
}

export default useBenefitTypesHelper;
